import { Controller } from "@hotwired/stimulus"

export default class CursorPaginationController extends Controller {
  static targets = ["loader", "scrollContainer"]

  connect() {
    this.loading = false
    this.loadedUrls = new Set()
    this.retryCount = new Map()
    this.retryTimer = null
    this.abortController = null

    this.handleIntersection = this.handleIntersection.bind(this)
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this)

    // Bootstrap XXL breakpoint used by overflow-xxl-auto.
    this.breakpoint = window.matchMedia("(min-width: 1400px)")
    this.breakpoint.addEventListener(
      "change",
      this.handleBreakpointChange
    )

    this.createObserver()
    this.observePaginationTrigger()
  }

  disconnect() {
    this.observer?.disconnect()
    this.abortController?.abort()

    this.breakpoint?.removeEventListener(
      "change",
      this.handleBreakpointChange
    )

    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
    }
  }

  loaderTargetConnected() {
    requestAnimationFrame(() => {
      this.observePaginationTrigger()
    })
  }

  get scrollRoot() {
    const isXxl = this.breakpoint
      ? this.breakpoint.matches
      : window.matchMedia("(min-width: 1400px)").matches

    if (isXxl && this.hasScrollContainerTarget) {
      return this.scrollContainerTarget
    }

    return this.element
  }

  createObserver() {
    this.observer?.disconnect()

    this.observer = new IntersectionObserver(
      this.handleIntersection,
      {
        root: this.scrollRoot,
        rootMargin: "0px",
        threshold: 0
      }
    )
  }

  observePaginationTrigger() {
    if (!this.observer || !this.hasLoaderTarget) return

    this.observer.disconnect()
    this.observer.observe(this.paginationTrigger)
  }

  // Start loading when the third-to-last post enters the viewport. This keeps
  // requests close to the bottom without depending on variable post heights.
  get paginationTrigger() {
    const postList = this.element.querySelector("#post_list")

    if (!postList) return this.loaderTarget

    const posts = postList.querySelectorAll(
      ':scope > turbo-frame[id^="repost_list_"]'
    )

    return posts[Math.max(0, posts.length - 5)] || this.loaderTarget
  }

  handleIntersection(entries) {
    const loaderIsVisible = entries.some(entry => {
      return entry.isIntersecting
    })

    if (loaderIsVisible) {
      this.loadNext()
    }
  }

  handleBreakpointChange() {
    this.createObserver()
    this.observePaginationTrigger()
  }

  async loadNext() {
    if (this.loading || !this.hasLoaderTarget) return

    const loader = this.loaderTarget
    const url = loader.dataset.url

    if (!url || this.loadedUrls.has(url)) return

    this.loading = true
    this.loadedUrls.add(url)
    this.observer?.disconnect()

    this.abortController = new AbortController()
    let succeeded = false

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        },
        cache: "no-store",
        credentials: "same-origin",
        signal: this.abortController.signal
      })

      if (!response.ok) {
        throw new Error(
          `Pagination failed with status ${response.status}`
        )
      }

      const html = await response.text()

      if (!html.trim()) {
        throw new Error("Pagination returned an empty response")
      }

      Turbo.renderStreamMessage(html)

      this.retryCount.delete(url)
      succeeded = true
    } catch (error) {
      if (error.name === "AbortError") return

      console.error("Cursor pagination error:", error)

      this.loadedUrls.delete(url)
      this.scheduleRetry(url)
    } finally {
      this.loading = false
      this.abortController = null

      if (succeeded) {
        requestAnimationFrame(() => {
          this.observePaginationTrigger()
        })
      }
    }
  }

  scheduleRetry(url) {
    const attempts = (this.retryCount.get(url) || 0) + 1

    this.retryCount.set(url, attempts)

    // Stop automatic retries after three failures.
    if (attempts > 3) return

    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
    }

    const retryDelay = attempts * 2000

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.observePaginationTrigger()
    }, retryDelay)
  }
}
